import clsx from 'clsx'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import split from 'lodash/split'
import { useRouter } from 'next/router'
import React, { useCallback, useContext, useMemo } from 'react'

import AngleLeftSVG from '@Assets/SVG/AngleLeft'
import AngleRightSVG from '@Assets/SVG/AngleRight'
import { MetadataContext } from '@Globals/Metadata'
import { Trans } from '@Globals/Translations'

import styles from './pagination.module.scss'

const generatePageNumbers = (
  currentPageNumber: number,
  totalPages: number,
  maxPageNumbers: number,
): number[] => {
  if (totalPages <= maxPageNumbers) {
    // If total pages are less than or equal to maxPageNumbers, return all pages.
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const halfMax = Math.floor(maxPageNumbers / 2)
  let startPage = Math.max(1, currentPageNumber - halfMax)
  let endPage = Math.min(totalPages, startPage + maxPageNumbers - 1)

  // Adjust if we're at the beginning or end of the list.
  if (endPage - startPage + 1 < maxPageNumbers) {
    endPage = Math.min(totalPages, startPage + maxPageNumbers - 1)
    startPage = Math.max(1, endPage - maxPageNumbers + 1)
  }

  const pageNumbers: number[] = []

  for (let i = startPage; i <= endPage; i += 1) {
    pageNumbers.push(i)
  }

  return pageNumbers
}

export const PaginationNumberList = ({ changePage, maxPageNumbers, page, pages }: {
  changePage: (page: number) => void
  maxPageNumbers: number
  page: number
  pages: number
}) => {
  const pageNumbers = generatePageNumbers(page, pages, maxPageNumbers)

  return (
    <div className={styles['number-list']}>
      <div>
        <button
          disabled={page === 1}
          onClick={(event) => {
            event.preventDefault()

            changePage(1)
          }}
          type="button"
        >
          ‹‹
        </button>
        {pageNumbers.map(pageNumber => (
          <button
            key={pageNumber}
            disabled={pageNumber === page}
            onClick={(event) => {
              event.preventDefault()

              changePage(pageNumber)
            }}
            type="button"
          >
            {pageNumber}
          </button>
        ))}
        <button
          disabled={page === pages}
          onClick={(event) => {
            event.preventDefault()

            changePage(pages)
          }}
          type="button"
        >
          ››
        </button>
      </div>
    </div>
  )
}

const PaginationButton = ({
  children,
  disabled = false,
  href,
  onClick,
}: {
  children: React.ReactNode
  disabled?: boolean
  href: string
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void
}) => {
  if (disabled) {
    return (
      <span className={clsx(styles.button, styles.disabled)}>
        {children}
      </span>
    )
  }

  return (
    <a
      className={clsx(styles.button)}
      href={href}
      onClick={onClick}
    >
      {children}
    </a>
  )
}

export const PaginationArrows = ({
  onClick,
  pages,
}: {
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void
  pages: number
}) => {
  const { asPath, locale, pathname, push, query } = useRouter()
  const { storeURL } = useContext(MetadataContext)

  const page = parseInt(query.page as string, 10) || 1
  const pageIndex = page - 1

  const pageURLs = useMemo(() => {
    const base = new URL(asPath, 'http://localhost')
    base.searchParams.delete('page')

    return Array.from({ length: pages }, (_, i) => {
      const pageNumber = i + 1
      if (pageNumber > 1) {
        base.searchParams.set('page', pageNumber.toString())
      }

      return storeURL(base.pathname + base.search)
    })
  }, [asPath, pages, storeURL])

  const changePage = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()

    const url = new URL(event.currentTarget.href)
    const pageString = url.searchParams.get('page')

    const nextQuery = omit(query, ['page'])

    if (pageString) {
      nextQuery.page = pageString
    }

    if (isEqual(query, nextQuery)) {
      return
    }

    push(
      {
        pathname,
        query: nextQuery,
      },
      {
        pathname: split(asPath, '?')[0],
        query: omit(nextQuery, ['slug']),
      },
      {
        locale,
        shallow: true,
        scroll: false,
      },
    )

    if (onClick) {
      onClick(event)
    }
  }, [asPath, locale, onClick, pathname, push, query])

  if (page < 1 || page > pages) {
    return null
  }

  return (
    <div className={styles.pagination}>
      {/* <PaginationButton
        disabled={pageIndex <= 0}
        href={pageURLs[0]}
        onClick={changePage}
      >
        First
      </PaginationButton> */}
      <PaginationButton
        disabled={pageIndex <= 0}
        href={pageURLs[pageIndex - 1]}
        onClick={changePage}
      >
        <AngleLeftSVG />
      </PaginationButton>

      <span className={styles.text}>
        <Trans
          i18nKey="common:PAGINATION_PAGE_OF_PAGES"
          values={{ page, pages }}
        />
      </span>

      <PaginationButton
        disabled={pageIndex >= pageURLs.length - 1}
        href={pageURLs[pageIndex + 1]}
        onClick={changePage}
      >
        <AngleRightSVG />
      </PaginationButton>
      {/* <PaginationButton
        disabled={pageIndex >= pageURLs.length - 1}
        href={pageURLs[pageURLs.length - 1]}
        onClick={changePage}
      >
        Last
      </PaginationButton> */}
    </div>
  )
}
